// frontend/packages/business-console/src/pages/Scheduling/StaffPanel.jsx
//
// Staff CRUD for the availability editor: name + IANA time zone + active flag.
// Selecting a staff member scopes the hours + time-off editors below it (the
// parent AvailabilityTab owns the selection). Config writes are
// businessadmin/manager server-side (F.10); we render for all and surface a
// 403 on save.

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import {
  createStaff,
  updateStaff,
  deleteStaff,
} from '../../services/schedulingService';
import { Surface, useTokens } from '../../components/primitives';

function serverMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

// A sensible default zone for a new staff member: the viewer's own zone.
function localZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export default function StaffPanel({ staff, selectedId, onSelect, onChanged }) {
  const t = useTokens();
  const [editing, setEditing] = useState(null); // { _id?, name, timeZone, active }
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const openCreate = () =>
    setEditing({ name: '', timeZone: localZone(), active: true });
  const openEdit = (m) =>
    setEditing({
      _id: m._id,
      name: m.name || '',
      timeZone: m.timeZone || localZone(),
      active: m.active !== false,
    });

  const save = async () => {
    if (!editing || busy) return;
    if (!editing.name.trim()) {
      setError('Enter a name.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name: editing.name.trim(),
        timeZone: editing.timeZone.trim(),
        active: editing.active,
      };
      if (editing._id) await updateStaff(editing._id, payload);
      else await createStaff(payload);
      setEditing(null);
      onChanged();
    } catch (err) {
      setError(serverMessage(err, "Couldn't save this staff member."));
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async () => {
    if (!deleteTarget || busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteStaff(deleteTarget._id);
      setDeleteTarget(null);
      onChanged();
    } catch (err) {
      setError(serverMessage(err, "Couldn't remove this staff member."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Surface sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Staff
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={openCreate}
        >
          Add staff
        </Button>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {staff.length === 0 ? (
        <Typography variant="body2" sx={{ color: t.muted }}>
          Add the people who take bookings. Each has their own hours and time
          off.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {staff.map((m) => {
            const selected = m._id === selectedId;
            return (
              <Box
                key={m._id}
                onClick={() => onSelect(m._id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: `1px solid ${
                    selected ? t.blueBorder : 'transparent'
                  }`,
                  bgcolor: selected ? t.blueBg : 'transparent',
                  '&:hover': { bgcolor: selected ? t.blueBg : t.subtle },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                    {m.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    {m.timeZone}
                  </Typography>
                </Box>
                {m.active === false ? (
                  <Chip size="small" label="Inactive" variant="outlined" />
                ) : null}
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    aria-label={`Edit ${m.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openEdit(m);
                    }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    aria-label={`Remove ${m.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(m);
                    }}
                  >
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* Create / edit dialog */}
      <Dialog
        open={Boolean(editing)}
        onClose={() => (busy ? null : setEditing(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editing?._id ? 'Edit staff member' : 'Add staff member'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Name"
              size="small"
              fullWidth
              value={editing?.name || ''}
              onChange={(e) =>
                setEditing((s) => ({ ...s, name: e.target.value }))
              }
              disabled={busy}
            />
            <TextField
              label="Time zone (IANA)"
              size="small"
              fullWidth
              helperText="For example America/Chicago"
              value={editing?.timeZone || ''}
              onChange={(e) =>
                setEditing((s) => ({ ...s, timeZone: e.target.value }))
              }
              disabled={busy}
            />
            <Box>
              <Button
                size="small"
                variant={editing?.active ? 'contained' : 'outlined'}
                onClick={() => setEditing((s) => ({ ...s, active: !s.active }))}
                disabled={busy}
              >
                {editing?.active ? 'Active' : 'Inactive'}
              </Button>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', ml: 1.5 }}
              >
                Inactive staff take no new bookings.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditing(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : null}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => (busy ? null : setDeleteTarget(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Remove {deleteTarget?.name || 'staff member'}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Their hours and time off are removed from booking. This can be
            restored by support.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={runDelete}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : null}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Surface>
  );
}
